import C from "@/views/enterprise/rg-object-history/Constants";
import { PLOT_CHILD_CATEGORY } from "@/config/const";
import Colors from "@/config/Colors.js";
import * as turf from "@turf/turf";
import mapKeys from "lodash/mapKeys";
import isObject from "lodash/isObject";

export default {
  isInitial: (state) => {
    return state.isInitial;
  },

  playbackSettings: (state) => {
    return state.playbackSettings;
  },

  isActiveDateInputDialog: (state) => {
    return state.isActiveDateInputDialog;
  },

  playbackBaseTime: (state) => {
    return state.playbackSettings.baseTime;
  },

  playbackStartTime: (state) => {
    return state.playbackSettings.startTime;
  },

  playbackEndTime: (state) => {
    return state.playbackSettings.endTime;
  },

  playbackTimeNow: (state) => {
    return state.playbackSettings.now;
  },

  playbackSecondsPerFrame: (state) => {
    return state.playbackSettings.secondsPerFrame;
  },

  playbackStatus: (state) => {
    return state.playbackSettings.playbackStatus;
  },

  layerFilters: (state) => {
    return state.layerFilters;
  },

  visibleTracks: (state) => {
    return state.visibleTracks;
  },

  visibleTrackVessels: ({ visibleTracks }) => {
    if (!isObject(visibleTracks)) return [];
    if (!Array.isArray(visibleTracks.vessels)) return [];
    return visibleTracks.vessels;
  },

  visibleTrackRgObjects({ visibleTracks }) {
    if (!isObject(visibleTracks)) return [];
    if (!Array.isArray(visibleTracks.rgObject)) return [];
    return visibleTracks.rgObject; // [id1(string), id2(string), ...]
  },

  hasVisibleTrackRgObjects: (_state, { visibleTrackRgObjects }) => {
    return visibleTrackRgObjects.length > 0;
  }, // 選択状態のRgObjectがあるか判定

  isVisibleTrack: (state) => (type, id) => {
    let found;
    if (C.PLAYBACK_OBJECT_TYPE.RG_OBJECT === type) {
      found = state.visibleTracks.rgObject.find((el) => el === id);
    } else {
      found = state.visibleTracks.vessels.find((el) => el === id);
    }

    return typeof found !== "undefined";
  },

  privatePlotFeatures({ privatePlots }, getters) {
    const now = new Date(getters.playbackTimeNow * 1000);
    const filteredPrivatePlots = privatePlots.filter((plot) => {
      // 削除済みの場合は削る
      if (plot.deleted_at && plot.deleted_at.toDate() < now) {
        return false;
      }
      // 作成日時に到達していないものは削る
      if (plot.created_at && now < plot.created_at.toDate()) {
        return false;
      }
      // 表示期間がないものは表示する
      if (!plot.start_date && !plot.end_date) {
        return true;
      }
      // 表示開始日時に到達してない場合は削る
      if (plot.start_date && now < plot.start_date.toDate()) {
        return false;
      }
      // 表示終了日時を過ぎた場合は削る
      if (plot.end_date && plot.end_date.toDate() < now) {
        return false;
      }
      return true;
    });
    return filteredPrivatePlots.reduce((acc, plot) => {
      // プライベートプロットのFeature(ポイント)
      if (plot.points.length === 1) {
        acc.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [plot.points[0].longitude, plot.points[0].latitude],
          },
          properties: {
            hash: plot.hash,
            category: String(
              PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
                .parent,
            ),
            title: plot.title,
            icon: PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
              .icon,
          },
        });
      }
      // プライベートプロットのFeature(ポリゴン)
      if (plot.points.length > 2) {
        acc.push({
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [
              plot.points.map((x) => {
                return [x.longitude, x.latitude];
              }),
            ],
          },
          properties: {
            hash: plot.hash,
            category: String(
              PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
                .parent,
            ),
            title: plot.title,
            color: Colors.getPlotSubCategoryColor(plot.plot_child_category_id),
            icon: PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
              .icon,
          },
        });
      }
      return acc;
    }, []);
  },

  /**
   * @returns {Record<string, Object>} 法人カメラのhashによるMap
   */
  cameraHashMap({ cameras }) {
    if (!Array.isArray(cameras)) return {};
    return mapKeys(
      cameras.filter((camera) => camera && camera.id && camera.hash),
      "hash",
    );
  },

  /**
   * @returns {Record<string, Object>} レーダカメラのマスタ
   */
  radarGridCameraMaster({ radarGridCameraMaster }) {
    if (!isObject(radarGridCameraMaster)) return {};
    return radarGridCameraMaster;
  },

  /**
   * 法人カメラのうち、レーダ用のものを持っているか
   * @returns {Boolean}
   */
  hasRadarGridCameras(_, { radarGridCameras }) {
    return Array.isArray(radarGridCameras) && radarGridCameras.length > 0;
  },

  /**
   * 法人カメラのうち、レーダ用のもの
   * @returns {Array<Object>} 法人カメラのリスト
   */
  radarGridCameras({ cameras, radarGridCameraMaster }, { cameraHashMap }) {
    if (!isObject(radarGridCameraMaster)) return [];
    if (!Array.isArray(cameras)) return [];
    return Object.values(radarGridCameraMaster)
      .filter(
        (cameraMaster) =>
          isObject(cameraMaster) && cameraHashMap[cameraMaster.a],
      )
      .map(({ a }) => cameraHashMap[a]);
  },

  /**
   * 法人カメラのうち、レーダ用のものを地図に表示するためのFeatureのリスト
   * @returns {Array<Feature>}
   */
  radarGridCameraMasterFeatures({ radarGridCameraMaster }, { cameraHashMap }) {
    if (!radarGridCameraMaster) return [];
    return Object.values(radarGridCameraMaster)
      .filter((cameraMaster) => isObject(cameraMaster))
      .filter(({ l, a }) => l && a)
      .filter(({ a }) => cameraHashMap[a])
      .map((cameraMaster) => {
        return turf.point([cameraMaster.l.longitude, cameraMaster.l.latitude], {
          cameraId: cameraMaster.ci,
          name: cameraHashMap[cameraMaster.a].name,
          direction: cameraMaster.p,
          icon: C.FIXED_POINT_CAMERA_HISTORY,
          isTextTop: true,
        });
      });
  },

  /**
   * 拡大表示中のRgObjectの情報を保持
   * @returns {Array<{id: string, src: string, trackNumber: string, aiseaCameraName: string}>} 拡大表示中のRgObjectの情報
   */
  expandImgRgObjects({ expandImgRgObjects }) {
    if (!Array.isArray(expandImgRgObjects)) return [];
    return expandImgRgObjects;
  },
};
